/**
 * Screen share recording manager that coordinates audio and screen StreamRecorder
 * instances to produce screen share videos for RLC SIM attempts.
 *
 * @module utils/screenShare
 * @author griboni@snowfly.com
 * @version 1.0.0
 */
import {uploadScreenShareStream} from "../services/result.service";
import {AudioRecorder, ScreenRecorder} from "./mediaRecorders";

/**
 * The RLCScreenRecorder class is designed for managing screen and audio recordings.
 * It provides functionalities to prepare, start, stop, pause, resume recordings,
 * and upload recorded streams to a remote server with progress tracking.
 */
export class RLCScreenRecorder {
    recorders = [];
    fileNames = [];
    videoSources = [];
    maxScreens = 1;
    options = {
        audioDeviceId: '',
        maxScreens: 1
    };
    #ready = false;

    /**
     * Creates an instance of the class and initializes recorders for audio and multiple screens.
     *
     * @param {Object} options An object containing configuration options for the instance.
     * @param {string} options.audioDeviceId The identifier for the audio device to be used.
     * @param {number|string} options.maxScreens The maximum number of screens to initialize recorders for.
     * @return {void}
     */
    constructor(options) {
        Object.assign(this.options, options);
        this.maxScreens = parseInt(this.options.maxScreens);

        this.recorders.push(new AudioRecorder(this.options.audioDeviceId));

        for (let screen = 0; screen < this.maxScreens; screen++) {
            this.recorders.push(new ScreenRecorder());
        }
    }

    /**
     * Prepares all recorders by invoking their respective prepare methods.
     * Aggregates the results into a list of file names and streams types.
     * Marks the instance as ready if all recorders are successfully prepared.
     *
     * @return {Promise<Array>} A Promise that resolves with an array of boolean values indicating the preparation status of each recorder.
     *                           The Promise rejects if any recorder fails to initialize, providing an error object with details.
     */
    async prepare() {
        let self = this;
        let promises = [];

        this.recorders.forEach((recorder) => {
            promises.push(
                new Promise((resolve, reject) => {
                    recorder.prepare().then((res) => {
                        if (res) {
                            self.#ready = true;
                            self.fileNames.push({
                                name: recorder.fileName,
                                type: recorder.streamType
                            });
                            resolve(true);
                        } else {
                            reject({error: 'Recorder failed initialization', recorder: recorder});
                        }
                    });
                })
            );
        });

        return await Promise.all(promises);
    }

    /**
     * Starts the recording process for each recorder in the collection.
     * Iterates through all the recorders and triggers their start method asynchronously.
     *
     * @return {Promise<void>} A promise that resolves when all recorders have started successfully.
     */
    async start() {
        for (const recorder of this.recorders) {
            await recorder.start();
        }
    }

    /**
     * Stops all active recorders and releases any related resources.
     * Iterates over each recorder in the collection, calling their stop and close methods asynchronously.
     * Once all recorders are stopped and closed, the recorders collection is cleared.
     *
     * @return {Promise<void>} A promise that resolves when all recorders have been stopped and cleared.
     */
    async stop() {
        for (const recorder of this.recorders) {
            await recorder.stop();
            await recorder.close();
        }
        this.recorders = [];
    }

    /**
     * Pauses all active recorders in the collection.
     * This method iterates over all recorders and invokes their respective pause method.
     *
     * @return {Promise<void>} A promise that resolves when all recorders have been paused.
     */
    async pause() {
        for (const recorder of this.recorders) {
            await recorder.pause();
        }
    }

    /**
     * Resumes the operation of all recorders that are part of this instance.
     * Calls the resume method on each recorder in the collection of recorders asynchronously.
     * Ensures each recorder continues from its paused state.
     *
     * @return {Promise<void>} A promise that resolves when all recorders have successfully resumed.
     */
    async resume() {
        for (const recorder of this.recorders) {
            await recorder.resume();
        }
    }

    /**
     * Uploads a series of files from the local storage to a remote server with progress tracking.
     *
     * The method processes a list of files obtained from the local filesystem. Each file is checked
     * for correct metadata and availability. If the file is correctly retrieved, it is uploaded with
     * progress feedback provided via the supplied callback function. The method returns the successfully
     * uploaded file information.
     *
     * @async
     * @param {function(number): Promise<void>} progressCB - A callback function that is invoked with the
     *                                                       current progress percentage during the upload process.
     *                                                       The function should accept a number (percentage progress)
     *                                                       and return a Promise.
     * @return {Promise<Object[]|null>} A Promise that resolves to an array of successfully uploaded file information
     *                                  (each object contains `name` and `type` of the uploaded file), or `null` if
     *                                  any errors occurred during the file uploads.
     */
    async uploadStreams(progressCB) {
        await navigator.storage.persist();

        const fsRoot = await navigator.storage.getDirectory();
        let sources = [];

        let files = [],
            totalSize = 0;
        for await (const recordingInfo of this.fileNames) {
            let fileName,
                fileType,
                fileHandle,
                fileSize,
                file;

            try {
                fileHandle = await fsRoot.getFileHandle(recordingInfo.name );
                file = await fileHandle.getFile();
                fileName = file.name;
                fileType = file.type;//recordingInfo.type === 'audio' ? 'audio/webm' : 'video/webm';
                fileSize = file.size;

                if (file.size === 0) {
                    fileName = recordingInfo.name + '.crswap'; // Not sure why this extension is needed in Chrome
                    let altFileHandle = await fsRoot.getFileHandle(fileName);
                    let altFile = await altFileHandle.getFile();

                    fileSize = altFile.size;
                }

                files.push({
                    name: fileName,
                    type: fileType,
                    size: fileSize,
                });
                totalSize += fileSize;
            } catch (e) {
                console.error(e);
            }
        }

        let totalChunks = totalSize / (512*1024),
            curChunk = 0,
            errors = false;

        /**
         * An asynchronous callback function that handles progress updates during an operation.
         *
         * @callback progressCallback
         * @param {Object} data - The data associated with the current operation progress.
         * @param {Object} response - The response data related to the progress.
         * @returns {Promise<void>} A Promise that resolves when the progress callback is complete.
         */
        const progressCallback = async (data, response) => {
            console.log('CB Data = %o', data);
            console.log('CB Response = %o', response);
            let percent = curChunk * 100 / totalChunks;
            if (progressCB) {
                await progressCB(percent);
            }
            curChunk += 1;
        };

        /**
         * Uploads a file to a remote server with progress tracking.
         *
         * The function takes an input representing a file entry and attempts to retrieve the file's metadata
         * and content. If the metadata or file handle is unavailable, it attempts to recover the necessary
         * information. Once the file data is successfully retrieved, it uploads the file using a specified
         * upload utility function `uploadScreenShareStream`.
         *
         * @async
         * @param {Object} fileEntry - The file entry object containing metadata about the file to be uploaded.
         * @param {string} fileEntry.name - The name of the file.
         * @param {string} fileEntry.type - The MIME type of the file.
         * @returns {Promise<Object|null>} A Promise that resolves to an object with the remote file path and type
         *                                 of the uploaded file upon successful upload, or `null` if the upload failed.
         */
        const uploadFn = async (fileEntry) => {
            let fileName,
                fileType,
                fileHandle,
                file,
                result = null;

            try {
                fileName = fileEntry.name;
                fileType = fileEntry.type;
                fileHandle = await fsRoot.getFileHandle(fileEntry.name);//await fsRoot.getFileHandle(fileName);
                file = await fileHandle.getFile();
            } catch (err) {
                fileName = fileEntry.name.replace('.crswap', '');
                fileHandle = await fsRoot.getFileHandle(fileEntry.name);//await fsRoot.getFileHandle(fileName);
                file = await fileHandle.getFile();
            }

            try {
                console.log('Uploading file: %s', fileName);
                console.log('fsRoot = %o', fsRoot);
                console.log('fileHandle = %o', fileHandle);
                console.log('file = %o', file);

                let response = await uploadScreenShareStream(file, fileType, fileName, progressCallback);
                if (response && response.complete) {
                    console.log('File %s uploaded successfully', fileName);
                    result = {
                        name: response.remotePath,
                        type: fileType
                    };
                }
            } catch (err) {
                console.error('Could not upload file %s: %o', fileName, err);
            }

            return result;
        };

        for await (const recordingInfo of files) {
            try {
                let uploadRec = await uploadFn(recordingInfo);
                if (uploadRec) {
                    console.log('File %o uploaded successfully', recordingInfo);
                    sources.push(uploadRec);
                } else {
                    errors = true;
                    console.log('File %o could not be uploaded', recordingInfo);
                }
            } catch (err) {
                errors = true;
                console.error('Could not upload file %o: %o', recordingInfo, err);
            }
        }

        this.videoSources = sources.slice(0);

        return errors ? null : this.videoSources;
    }

    /**
     * Clears all streams in the root directory by recursively removing entries.
     *
     * @return {Promise<void>} A promise that resolves once all streams have been cleared.
     */
    async clearStreams() {
        let fsRoot = await navigator.storage.getDirectory();
        for await (let value of Object.values(fsRoot)) {
            await fsRoot.removeEntry(value, {recursive: true});
        }
    }

    /**
     * Determines the readiness state of all recorders.
     * Iterates through the list of recorders and checks the 'ready' status for each.
     * Returns true if all recorders are ready, otherwise returns false.
     *
     * @return {boolean} - A boolean indicating whether all recorders are ready.
     */
    get ready() {
        let result = true;
        for (const recorder of this.recorders) {
            result &= recorder.ready;
        }
        return result;
    }

}
